import { useState, useEffect } from 'react';
import { TrendingUp, Users, CheckCircle2, AlertTriangle, Clock, BarChart3, Loader2, DollarSign, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { projectId } from '../utils/supabase/info';

interface ConsultantAnalyticsDashboardProps {
  accessToken: string;
  /** `portfolio`: status, hours and revenue (Firm › Portfolio); `activity`: the activity trend (Deliver › Analytics); `all`: both. */
  sections?: 'all' | 'portfolio' | 'activity';
}

interface AnalyticsData {
  totalClients: number;
  totalConfigItems: number;
  totalCompleted: number;
  totalInProgress: number;
  totalBlocked: number;
  totalWaitingConsultant: number;
  totalWaitingClient: number;
  avgCompletionRate: number;
  clientsAtRisk: number;
  clientsHealthy: number;
  activityTrend: Array<{
    date: string;
    activity: number;
    completions: number;
  }>;
}

interface TimeTrackingData {
  totalHours: number;
  billableHours: number;
  totalRevenue: number;
  utilizationRate: number;
  byCompany: Record<string, { companyName: string; hours: number; revenue: number }>;
  bySow: Record<string, { sowName: string; companyName: string; hours: number; revenue: number }>;
  dailyBreakdown: Array<{
    date: string;
    hours: number;
    revenue: number;
  }>;
}

export function ConsultantAnalyticsDashboard({ accessToken, sections = 'all' }: ConsultantAnalyticsDashboardProps) {
  const show = { portfolio: sections !== 'activity', activity: sections !== 'portfolio' };
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [timeTracking, setTimeTracking] = useState<TimeTrackingData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load both analytics and time tracking data in parallel
      const [analyticsRes, timeTrackingRes] = await Promise.all([
        fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/analytics`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        ),
        fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/analytics/time-entries`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        ),
      ]);

      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setAnalytics(data);
      }

      if (timeTrackingRes.ok) {
        const data = await timeTrackingRes.json();
        setTimeTracking(data);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !analytics || !timeTracking) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  const maxActivity = Math.max(...analytics.activityTrend.map(d => d.activity), 1);

  return (
    <div className="space-y-6">
      {show.portfolio && (<>
      {/* Top-level Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Clients */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Total Clients</p>
              <Users className="size-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold">{analytics.totalClients}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                {analytics.clientsHealthy} Healthy
              </Badge>
              {analytics.clientsAtRisk > 0 && (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                  {analytics.clientsAtRisk} At Risk
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Avg Completion Rate */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Avg Completion</p>
              <TrendingUp className="size-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-600">{analytics.avgCompletionRate}%</p>
            <Progress value={analytics.avgCompletionRate} className="mt-2 h-2" />
          </CardContent>
        </Card>

        {/* Items Needing Attention */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Needs Your Review</p>
              <Clock className="size-5 text-orange-600" />
            </div>
            <p className="text-3xl font-bold text-orange-600">{analytics.totalWaitingConsultant}</p>
            <p className="text-xs text-muted-foreground mt-2">
              Across all clients
            </p>
          </CardContent>
        </Card>

        {/* Total Items */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Total Config Items</p>
              <BarChart3 className="size-5 text-purple-600" />
            </div>
            <p className="text-3xl font-bold">{analytics.totalConfigItems}</p>
            <p className="text-xs text-muted-foreground mt-2">
              {analytics.totalCompleted} completed
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Progress Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Portfolio Status</CardTitle>
          <CardDescription>Overall status across all client engagements</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-900">Completed</span>
                <CheckCircle2 className="size-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-600">{analytics.totalCompleted}</p>
              <p className="text-xs text-green-700 mt-1">
                {analytics.totalConfigItems > 0 
                  ? Math.round((analytics.totalCompleted / analytics.totalConfigItems) * 100)
                  : 0}% of total
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">In Progress</span>
                <Clock className="size-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-600">{analytics.totalInProgress}</p>
              <p className="text-xs text-blue-700 mt-1">
                {analytics.totalWaitingClient} with client
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-orange-900">Action Needed</span>
                <AlertTriangle className="size-5 text-orange-600" />
              </div>
              <p className="text-2xl font-bold text-orange-600">{analytics.totalWaitingConsultant}</p>
              <p className="text-xs text-orange-700 mt-1">
                Awaiting your review
              </p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-red-900">Blocked</span>
                <AlertTriangle className="size-5 text-red-600" />
              </div>
              <p className="text-2xl font-bold text-red-600">{analytics.totalBlocked}</p>
              <p className="text-xs text-red-700 mt-1">
                Require resolution
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      </>)}

      {show.activity && (<>
      {/* Activity Trend */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Trend (Last 7 Days)</CardTitle>
          <CardDescription>Daily activity and completion metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {analytics.activityTrend.map((day, index) => {
              const date = new Date(day.date);
              const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
              const barWidth = (day.activity / maxActivity) * 100;

              return (
                <div key={day.date}>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-sm font-medium w-12">{dayName}</span>
                    <div className="flex-1 relative h-8 bg-gray-100 rounded overflow-hidden">
                      {day.activity > 0 && (
                        <>
                          <div
                            className="absolute inset-y-0 left-0 bg-blue-500 opacity-30"
                            style={{ width: `${barWidth}%` }}
                          />
                          <div className="absolute inset-0 flex items-center px-2">
                            <span className="text-xs font-medium">
                              {day.activity} {day.activity === 1 ? 'activity' : 'activities'}
                            </span>
                          </div>
                        </>
                      )}
                      {day.activity === 0 && (
                        <div className="absolute inset-0 flex items-center px-2">
                          <span className="text-xs text-muted-foreground">No activity</span>
                        </div>
                      )}
                    </div>
                    {day.completions > 0 && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                        <CheckCircle2 className="size-3 mr-1" />
                        {day.completions}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      </>)}

      {show.portfolio && (<>
      {/* Time Tracking Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Hours */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Total Hours</p>
              <Clock className="size-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold">{timeTracking.totalHours.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-2">
              Across all clients
            </p>
          </CardContent>
        </Card>

        {/* Billable Hours */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Billable Hours</p>
              <Clock className="size-5 text-blue-600" />
            </div>
            <p className="text-3xl font-bold">{timeTracking.billableHours.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-2">
              Across all clients
            </p>
          </CardContent>
        </Card>

        {/* Total Revenue */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <DollarSign className="size-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold">${timeTracking.totalRevenue.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-2">
              Across all clients
            </p>
          </CardContent>
        </Card>

        {/* Utilization Rate */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Utilization Rate</p>
              <TrendingUp className="size-5 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-600">{timeTracking.utilizationRate.toFixed(2)}%</p>
            <Progress value={timeTracking.utilizationRate} className="mt-2 h-2" />
          </CardContent>
        </Card>
      </div>

      {/* Time Tracking Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Time Tracking Breakdown (Last 7 Days)</CardTitle>
          <CardDescription>Daily hours and revenue breakdown</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {timeTracking.dailyBreakdown.map((day, index) => {
              const date = new Date(day.date);
              const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
              const barWidth = (day.hours / timeTracking.totalHours) * 100;

              return (
                <div key={day.date}>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-sm font-medium w-12">{dayName}</span>
                    <div className="flex-1 relative h-8 bg-gray-100 rounded overflow-hidden">
                      {day.hours > 0 && (
                        <>
                          <div
                            className="absolute inset-y-0 left-0 bg-blue-500 opacity-30"
                            style={{ width: `${barWidth}%` }}
                          />
                          <div className="absolute inset-0 flex items-center px-2">
                            <span className="text-xs font-medium">
                              {day.hours.toFixed(2)} hours
                            </span>
                          </div>
                        </>
                      )}
                      {day.hours === 0 && (
                        <div className="absolute inset-0 flex items-center px-2">
                          <span className="text-xs text-muted-foreground">No hours</span>
                        </div>
                      )}
                    </div>
                    {day.revenue > 0 && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                        <DollarSign className="size-3 mr-1" />
                        ${day.revenue.toFixed(2)}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      </>)}
    </div>
  );
}